/**
 * @fileoverview Base class for database searchers (White, Yellow, Reverse, Advanced).
 * Handles PoiDataManager searches, map layer updates, and search history.
 * @module searchers/DbSearcher
 */

import { Searcher } from './Searcher.js';
import { queueDelayedSearchWarning, buildEmptySearchResultStructure, updateSearchPackWithSearchResult, buildSearchResult, anyFieldMatches } from '../utils/jrfuncs.js';
import { DEBUG_CBM, MAPAPP_DEFAULTS, RESULT_MANAGER_CONSTANTS } from '../constants.js';
import { preProcessQueryParseOptionsDirectives } from '../utils/jrdslquery.js';


/**
 * Base class for searchers that query PoiDataManager.
 *
 * Provides:
 * - Database search via PoiDataManager methods
 * - Map result layer management
 * - Search history integration
 * - Delayed search save for live typing
 *
 * Subclasses: WhiteSearcher, YellowSearcher, ReverseSearcher, AdvancedSearcher
 *
 * @class
 * @extends Searcher
 */
export class DbSearcher extends Searcher {
    /**
     * @param {Object} mapApp - The MapApp instance
     * @param {Object} config - Configuration options
     * @param {string} [config.searcherName] - Base name (derives searchType, mapLayerName, DOM IDs)
     * @param {string} [config.searchType] - Search type identifier (default: searcherName)
     * @param {Function} config.searchMethod - Search method function reference
     * @param {Object} config.searchMethodOptions - passed to compare function
     * @param {number} [config.minQueryLength] - Minimum query length to trigger search
     * @param {number} [config.resultLimitShowAll] - Max results for "show all"
     * @param {number} [config.resultLimitDefault] - Default result limit
     * @param {number} [config.resultLimitEnterKey] - Limit when Enter key pressed
     */
    constructor(mapApp, config = {}) {
        super(mapApp, {
            ...config
        });

        // Search-specific configuration - searchType defaults to searcherName
        this.searchType = config.searchType ?? config.searcherName ?? null;
        this.searchMethod = config.searchMethod || null;
        this.searchMethodOptions = config.searchMethodOptions || {};
        this.minQueryLength = config.minQueryLength ?? MAPAPP_DEFAULTS.MIN_QUERY_LENGTH;

        // Search result limits
        this.resultLimitShowAll = config.resultLimitShowAll || RESULT_MANAGER_CONSTANTS.NO_LIMIT_RESULT_COUNT;
        this.resultLimitDefault = config.resultLimitDefault || MAPAPP_DEFAULTS.RESULT_LIMIT_DEFAULT_INITIAL_DB_SEARCH;
        this.resultLimitEnterKey = config.resultLimitEnterKey || this.resultLimitShowAll;
    }




    // ==========================================
    // Db Searcher Accessors
    // ==========================================




    /**
     * Checks if this searcher allows POI-type results.
     * Database searchers always allow POI results.
     * @returns {boolean} True
     * @override
     */
    doesAllowResultTypePoi() {
        return true;
    }

    /**
     * Checks if this searcher allows search-type results (saved searches).
     * Database searchers do not store saved searches in their results.
     * @returns {boolean} False
     * @override
     */
    doesAllowResultTypeSearch() {
        return false;
    }

    /**
     * Gets results for database searchers.
     * Returns the direct result list (no filtering needed for db searches).
     * @returns {Array} Array of result items
     * @override
     */
    getInternalFilteredResultsIfAppropriate() {
        return this.lister.getInternalResults();
    }

    /**
     * Returns true since this is a database searcher.
     * @returns {boolean} True
     * @override
     */
    getIsDbSearcher() {
        return true;
    }




    /**
     * Clears all results without confirmation for database searchers.
     * Overrides the base class to skip the confirmation dialog.
     * @override
     */
    confirmThenClearAllResults() {
        // do not need to confirm in this case just clear
        this.performClearSearch(true)
        //this.lister.clearResultList();
        //this.clearMapResultLayer();
        //this.onSearchCleared();

    }
    

    // ==========================================
    // Core Search Operations
    // ==========================================

    /**
     * Perform a database search operation.
     * Delegates to performDbSearchDoWork after validating query length.
     * @param {Object} searchPack - The search pack with query, isEnterKey, explicitLimit, etc.
     * @override
     */
    async performSearchWork(searchPack) {
        // don't search if too short?
        // ATTN: maybe bypass this if enter key is pressed?
        const {query, isEnterKey, explicitLimit} = searchPack;
        //
        if (!query || query.trim().length < this.minQueryLength) {
            this.lister.clearResultList();
            this.clearMapResultLayer();
            this.onSearchCleared();
            return;
        }

        // Use higher limit when search is triggered by Enter key, or explicit limit if provided
        const limit = explicitLimit || (isEnterKey ? this.resultLimitEnterKey : this.resultLimitDefault);
        searchPack.limit = limit;

        // Show loading state from here? or let searchMethods do it
        if (false) {
            await this.showLoading();
        }

        // Search using PoiDataManager
        try {
            await this.performDbSearchDoWork(searchPack);
        } catch (error) {
            console.error(`ERROR: ${this.searchType} search error:`, error);
        }
    }



    /**
     * Execute the actual database search and update results.
     * Runs pre-search modifications, executes the search method, sorts results,
     * updates the lister, and updates the map layer.
     * Override in subclasses for custom search behavior.
     * @param {Object} searchPack - The search pack with query, limit, and options
     * @protected
     */
    async performDbSearchDoWork(searchPack) {
        const searchMethod = this.searchMethod;
   
        // generic PRE-SEARCH?
        this.performDbPreSearchOptionsModifications(searchPack);


        // run the search!
        const searchResult = await this.runSearchMethod(searchMethod, searchPack);
        if (searchResult==null) {
            // returning null means DO NOTHING dont update anything
            // this should only happen on some advanced search processing
            return;
        }

        //if (DEBUG_CBM) {
        //    console.log(`DEBUG: performDbSearchDoWork search result is "${JSON.stringify(searchResult)}"`);
        //}

        // sort
        this.doSortSearchResult(searchResult);

        // NOW (NEW); update searchPack with result summary!
        updateSearchPackWithSearchResult(searchPack, searchResult);

        // Set the results and include a re-run search callback
        this.lister.setAllResultsFromSearchPack(searchResult, searchPack);

        //console.log('ATTN:DEBUG RETURNING EARLY 2.');
        //return;

        // Update map layer with results
        this.updateMapResultLayer(searchResult.results);

        if (searchResult.error) {
            // no need to queue it, it will be display in result header
            // queueDelayedSearchWarning(searchResult.error);
        }
    }








    // ==========================================
    // PROXIES
    // ==========================================

    /**
     * Execute a search using the provided search method function.
     * Calls the search method bound to this searcher instance.
     * @param {Function} searchMethod - Search method function to invoke
     * @param {Object} searchPack - The search pack with query and options
     * @returns {Promise<Object>} Search result object with results array and totalCount
     */
    async runSearchMethod(searchMethod, query, searchOptions) {
        return await searchMethod.call(this, query, searchOptions);
    }








    // ==========================================
    // Post-Search Operations
    // ==========================================

    /**
     * Performs operations after completing a search.
     * Handles history saving based on search type and user action.
     * Saves immediately on Enter key; schedules delayed save otherwise.
     * @param {Object} searchPack - The search pack with query, isEnterKey, flagUserInitiatedThisSearch, explicitLimit, totalCount
     * @override
     */
    performPostSearchOperations(searchPack) {
        // destructure searchPack
        const {query, isEnterKey, flagUserInitiatedThisSearch, explicitLimit, totalCount} = searchPack;
        //
        if (query && flagUserInitiatedThisSearch && totalCount > 0) {
            if (isEnterKey || explicitLimit === this.resultLimitShowAll) {
                // Save search immediately
                const debugMessage = `post run auto search save to history`;
                this.doSaveSearchQuery(this.mapApp.getHistorySearcher(), searchPack, debugMessage);
                this.mapApp.logPlaySearchRun(searchPack);
            } else {
                // Schedule delayed history save
                this.mapApp.scheduleDelayedSearchSave(this, searchPack);
            }
        }

        // call super class
        super.performPostSearchOperations(searchPack);
    }




    // ==========================================
    // DERIVED CLASS SEARCH HOOKS
    // ==========================================

    /**
     * Hook for subclasses to modify search options before a database search is executed.
     * For example, AdvancedSearcher uses this to inject the selected region.
     * @param {Object} searchPack - The search pack to modify
     * @param {Object} [searchOptions] - Additional search options
     */
    performDbPreSearchOptionsModifications(searchPack, searchOptions) {
        // nothing to do in base class
    }









    // ==========================================
    // Delayed Search Save
    // ==========================================

    /**
     * Clears any pending delayed search save timer.
     * Called at the start of a new search to cancel pending auto-save on typing.
     * @override
     */
    clearDelayedLiveSearchSaveTrigger() {
        if (this.delayedLiveSearchSaveTimeoutId) {
            clearTimeout(this.delayedLiveSearchSaveTimeoutId);
            this.delayedLiveSearchSaveTimeoutId = null;
        }
    }

    /**
     * Schedules a delayed save of the search query to history.
     * Allows history update even when user doesn't press Enter.
     * @param {Object} searchPack - The search pack containing the query to save
     */
    scheduleDelayedLiveSearchSaveTrigger(searchPack) {
        // Clear any prior
        this.clearDelayedLiveSearchSaveTrigger();

        // If blank do nothing
        if (!searchPack.query) return;

        // Schedule it
        this.delayedLiveSearchSaveTimeoutId = setTimeout(() => {
            this.triggerDelayedLiveSearchSaveTrigger(searchPack);
        }, MAPAPP_DEFAULTS.LIVE_SEARCH_SAVE_DELAY_MS);
    }

    /**
     * Triggers the delayed search save, clearing the timer and executing the save.
     * @param {Object} searchPack - The search pack to save to history
     */
    triggerDelayedLiveSearchSaveTrigger(searchPack) {
        this.clearDelayedLiveSearchSaveTrigger();
        const debugMessage = `delayed live search save to history`;
        this.doSaveSearchQuery(this.mapApp.getHistorySearcher(), searchPack, debugMessage);
        this.mapApp.logPlaySearchRun(searchPack);
    }




    // ==========================================
    // Cleanup
    // ==========================================

    /**
     * Destroys the searcher and cleans up resources.
     * Clears any pending timers and destroys the searcher.
     * @override
     */
    destroy() {
        this.clearDelayedLiveSearchSaveTrigger();
        super.destroy();
    }




    // ==========================================
    // Search Methods
    // ==========================================






    /**
     * Core search method that uses a custom match function on a specific set of fields.
     * Iterates over data items, testing each field value against the match function.
     * Respects limit, region filter, and random directives from the search pack.
     * @param {Object} searchPack - The search pack with query, limit, jregion, and options
     * @param {Function} matchFn - Matching function (fieldValue, searchPack, fieldName) => boolean
     * @returns {Object} Search result object with results array and totalCount
     */
    searchDataWithFieldMatchFunction(searchPack, matchFn) {
        //
        const results = [];
        let totalCount = 0;

        // Parse options directives (like overriding limit)
        let error = preProcessQueryParseOptionsDirectives(searchPack);
        if (error) return buildEmptySearchResultStructure(error);

        // destructure searchPack now, AFTER we have called preProcessQueryParseOptionsDirectives
        const {limit, jregion} = searchPack;
        const {fields} = this.searchMethodOptions;

        // get data source (will also randomize if random option is set)
        const dataSource = this.getPoiDataManager().getDataSourceForSearch(searchPack);

        // Normalize query
        searchPack.query = searchPack.query.toLowerCase().trim();

        // Check if empty
        if (!searchPack.query) {
            return buildEmptySearchResultStructure();
        }

        // Search through data
        for (const item of dataSource) {
            if (jregion && jregion != item.jregion) {
                continue;
            }
            const itemMatches = anyFieldMatches(item, fields, (value, field) => matchFn(value, searchPack, field));

            if (itemMatches) {
                totalCount++;
                if (results.length < limit) {
                    results.push(item);
                }
            }
        }

        return buildSearchResult(results, totalCount);
    }


    /**
     * Like searchDataWithFieldMatchFunction, but an item matches only if EVERY word in wordList matches some field.
     * @param {Object} searchPack - The search pack with query, limit, jregion, and options
     * @param {Function} matchFn - Matching function (fieldValue, word, fieldName) => boolean
     * @param {string[]} wordList - Words that must all match
     * @returns {Object} Search result object with results array and totalCount
     */
    searchDataWithWordsAgainstFields(searchPack, matchFn, wordList) {
        //
        const results = [];
        let totalCount = 0;

        // Parse options directives (like overriding limit)
        let error = preProcessQueryParseOptionsDirectives(searchPack);
        if (error) return buildEmptySearchResultStructure(error);

        // destructure searchPack now, AFTER we have called preProcessQueryParseOptionsDirectives
        const {limit, jregion} = searchPack;
        const {fields} = this.searchMethodOptions;

        // get data source (will also randomize if random option is set)
        const dataSource = this.getPoiDataManager().getDataSourceForSearch(searchPack);

        // Normalize query
        searchPack.query = searchPack.query.toLowerCase().trim();


        // Search through data
        let itemMatches;
        for (const item of dataSource) {
            if (jregion && jregion != item.jregion) {
                continue;
            }

            // now walk through ALL words in wordList, ALL must match sometwhere
            itemMatches = false;
            for (const word of wordList) {
                itemMatches = anyFieldMatches(item, fields, (value, field) => matchFn(value, word, field));
                // this is the test of the current word in loop; if any word FAILS on this item, we break and its false
                if (!itemMatches) {
                    break;
                }
            }

            if (itemMatches) {
                totalCount++;
                if (results.length < limit) {
                    results.push(item);
                }
            }
        }

        return buildSearchResult(results, totalCount);
    }





    /**
     * Core search method that uses an item-level match function. Used by advanced search.
     * Unlike searchDataWithFieldMatchFunction, this doesn't iterate fields -
     * the match function receives the entire item.
     * @param {Object} searchPack - The search pack with query, limit, jregion, and options
     * @param {Function} matchFn - Matching function (item) => boolean
     * @returns {Object} Search result object with results array, totalCount, and optional error
     */
    searchDataWithItemMatchFunction(searchPack, matchFn) {
        const results = [];
        let totalCount = 0;

        // get data source (will also randomize if random option is set)
        const dataSource = this.getPoiDataManager().getDataSourceForSearch(searchPack);

        // any limit set?
        const { limit } = searchPack;
        //console.log(`ATTN: test search limit = ${limit}.`)

        // is there a required region specified explicitly?
        const jregion = searchPack.jregion;

        try {
            for (const item of dataSource) {
                if (jregion && jregion != item.jregion) {
                    continue;
                }
                if (matchFn(item)) {
                    totalCount++;
                    if (results.length < limit) {
                        results.push(item);
                    }
                }
            }
            return buildSearchResult(results, totalCount);
        }  catch (e) {
            // The user sees the message only; the stack trace goes to the console for whoever
            // debugs it. (The header used to show e.stack, a full JavaScript stack trace, and the
            // brief branch named a misspelled, undeclared errorMessageBreif.)
            console.error('Advanced search failed while evaluating the query:', e);
            return buildSearchResult(results, totalCount, 'ERROR: ' + (e.message || String(e)));
        }
    }





    /**
     * Rebuilds and re-displays the result list without re-running the database search.
     * Re-renders the existing results using the last search pack.
     * @override
     */
    reBuildReDisplayResultList() {
        // rebuild the results but no strict need to re-run a db search
        // Set the results and include a re-run search callback
        const searchPack = this.getLastSearchPack();
        this.lister.renderAllCurrentResults(searchPack);
    }





    // ==========================================
    // Search Saving Helpers
    // ==========================================

    /**
     * Saves the current search to the SavedSearcher.
     * Uses the last query and result count from the most recent search.
     */
    saveLastSearchToSavedSearcher() {
        this.doSaveSearchQuery(this.mapApp.getSavedSearchSearcher(), this.getLastSearchPack(), `saved ${this.searchType} LAST search manually`);
    }

    /**
     * Saves a search query to a target searcher (either history or saved search).
     * Called when Enter key is pressed or "show all" is clicked.
     * @param {IntSearcher} searcher - The target searcher to save to (HistorySearcher or SavedSearcher)
     * @param {Object} searchPack - The search pack containing the query and metadata
     * @param {string} debugMessage - Debug message for logging
     */
    doSaveSearchQuery(searcher, searchPack, debugMessage) {
        searcher.addSavedSearchItemManually(searchPack, debugMessage);
   }







}
